import { AbortError } from '@shopify/cli-kit/node/error';
import { findFileWithExtension, replaceFileContent } from '../../file.js';
import { importLangAstGrep } from '../../ast.js';

async function replaceRootLinks(appDirectory, formatConfig, importer) {
  const { filepath, astType } = await findFileWithExtension(appDirectory, "root");
  if (!filepath || !astType) {
    throw new AbortError(`Could not find root file in ${appDirectory}`);
  }
  await replaceFileContent(filepath, formatConfig, async (content) => {
    const importStatement = `import ${importer.isDefault ? importer.name : `{${importer.name}}`} from '${(importer.isAbsolute ? "" : "./") + importer.path}';`;
    if (content.includes(importStatement.split("from")[0])) {
      return;
    }
    const astGrep = await importLangAstGrep(astType);
    const root = astGrep.parse(content).root();
    const importNodes = root.findAll({ rule: { kind: "import_statement" } }).reverse();
    const lastImportNode = importNodes.find((node) => node.text().includes(".css")) || importNodes.shift();
    const layoutStyleNode = root.find({
      rule: {
        kind: "jsx_element",
        regex: "resetStyles",
        has: {
          kind: "jsx_opening_element",
          has: {
            kind: "identifier",
            pattern: "link"
          }
        }
      }
    });
    if (!lastImportNode || !layoutStyleNode) {
      throw new AbortError(
        'Could not find a "links" export in root file. Please add one and try again.'
      );
    }
    const lastImportContent = lastImportNode.text();
    const layoutStyleNodeContent = layoutStyleNode.text();
    const newLinkNode = importer.isConditional ? `{${importer.name} && <link rel="stylesheet" href={${importer.name}}></link>}` : `<link rel="stylesheet" href={${importer.name}}></link>`;
    return content.replace(lastImportContent, lastImportContent + "\n" + importStatement).replace(
      layoutStyleNodeContent,
      newLinkNode + "\n" + layoutStyleNodeContent
    );
  });
}
async function injectVitePlugin(rootDirectory, formatConfig, importer, pluginOptions) {
  const { filepath, astType } = await findFileWithExtension(
    rootDirectory,
    "vite.config"
  );
  if (!filepath || !astType) {
    throw new AbortError(`Could not find vite.config file in ${rootDirectory}`);
  }
  await replaceFileContent(filepath, formatConfig, async (content) => {
    const importStatement = `import ${importer.isDefault ? importer.name : `{${importer.name}}`} from '${importer.path}';`;
    if (new RegExp(`['"]${importer.path.replace("/", "\\/")}['"]`).test(content)) {
      return;
    }
    const astGrep = await importLangAstGrep(astType);
    const root = astGrep.parse(content).root();
    const lastImportNode = root.findAll({ rule: { kind: "import_statement" } }).pop();
    const vitePluginListNode = root.find(vitePluginListRule);
    if (!lastImportNode || !vitePluginListNode) {
      throw new AbortError(
        'Could not find a "plugins" key in Vite config file. Please add one and try again.'
      );
    }
    const lastImportContent = lastImportNode.text();
    const linksExportReturnContent = vitePluginListNode.text();
    const newVitePluginItem = `${importer.name}(${pluginOptions ? JSON.stringify(pluginOptions) : ""})`;
    return content.replace(lastImportContent, lastImportContent + "\n" + importStatement).replace(
      linksExportReturnContent,
      linksExportReturnContent.replace("[", `[${newVitePluginItem},`)
    );
  });
}
const vitePluginListRule = {
  rule: {
    // An array
    pattern: "[$$$]",
    inside: {
      // directly in the value part of a `plugins` key
      kind: "pair",
      stopBy: "neighbor",
      has: {
        field: "key",
        regex: "^plugins$",
        stopBy: "neighbor"
      },
      inside: {
        // directly inside an object (the Vite config object)
        kind: "object",
        stopBy: "neighbor",
        // that is exported but is not inside another object
        // e.g. `export default {something:{plugins:[]}}`
        // doesn't match, but `export default {plugins:[]}` does.
        // And `export default defineConfig({plugins:[]})` matches too.
        all: [
          {
            inside: {
              kind: "export_statement",
              regex: "export default",
              stopBy: "end"
            }
          },
          {
            not: {
              inside: {
                kind: "object",
                stopBy: "end"
              }
            }
          }
        ]
      }
    }
  }
};

export { injectVitePlugin, replaceRootLinks };
